import { makeAutoObservable } from "mobx";
import dataRequest from "../http/DataRequest";
import {
  extractRestaurantGroups,
  getRestaurantGroupFromRow,
} from "../utils/iikoOlapGroup";

function extractGroupsFromWaitersResponse(response) {
  const set = new Set();
  for (const w of response?.waiters ?? []) {
    for (const d of w.datas ?? []) {
      const g = d.restaurantGroup ?? getRestaurantGroupFromRow(d);
      if (g) set.add(g);
    }
  }
  return [...set].sort((a, b) => a.localeCompare(b, "ru"));
}

function parseWaitersResponse(response, groupByRestaurant, selectedGroup) {
  if (!groupByRestaurant) {
    return {
      waiters: response?.waiters ?? [],
      overallTotalSum: response?.overallTotalSum ?? 0,
      byRestaurant: false,
    };
  }

  const flat = [];
  for (const w of response?.waiters ?? []) {
    for (const d of w.datas ?? []) {
      const restaurantGroup = d.restaurantGroup ?? getRestaurantGroupFromRow(d) ?? "—";
      if (selectedGroup && restaurantGroup !== selectedGroup) continue;
      flat.push({
        waiterName: w.waiterName,
        restaurantGroup,
        totalSum: Number(d.sumInt) || 0,
      });
    }
  }
  flat.sort((a, b) => b.totalSum - a.totalSum);
  const overallTotalSum = flat.reduce((s, r) => s + r.totalSum, 0);
  return { waiters: flat, overallTotalSum, byRestaurant: true };
}

class WaitersStore {
  waiters = [];
  overallTotalSum = 0;
  isLoading = false;
  isError = false;
  groupByRestaurant = false;
  byRestaurant = false;
  restaurantGroups = [];
  selectedRestaurantGroup = "";
  waitersRawResponse = null;
  fromDate = null;
  toDate = null;

  constructor() {
    makeAutoObservable(this);
  }

  setFromDate(date) {
    this.fromDate = date;
  }

  setToDate(date) {
    this.toDate = date;
  }

  getWaiterDailyData(waiterName, restaurantGroup = null) {
    const w = (this.waitersRawResponse?.waiters ?? []).find(
      (x) => x.waiterName === waiterName,
    );
    if (!w) return [];
    let datas = w.datas ?? [];
    if (restaurantGroup) {
      datas = datas.filter((d) => {
        const g = d.restaurantGroup ?? getRestaurantGroupFromRow(d);
        return g === restaurantGroup;
      });
    }
    return [...datas].sort((a, b) => String(a.date).localeCompare(String(b.date)));
  }

  setWaiters(waiters) {
    this.waiters = waiters;
  }

  setOverallTotalSum(overallTotalSum) {
    this.overallTotalSum = overallTotalSum;
  }

  setLoading(load) {
    this.isLoading = load;
  }

  setError(errors) {
    this.isError = errors;
  }

  setGroupByRestaurant(value) {
    const next = Boolean(value);
    if (this.groupByRestaurant === next) return;
    this.groupByRestaurant = next;
    if (!next) {
      this.selectedRestaurantGroup = "";
      this.restaurantGroups = [];
    }
    this.applyWaitersFromRaw();
  }

  setSelectedRestaurantGroup(name) {
    this.selectedRestaurantGroup = name || "";
    this.applyWaitersFromRaw();
  }

  syncRestaurantGroups() {
    const merged = extractGroupsFromWaitersResponse(this.waitersRawResponse);
    this.restaurantGroups = merged;
    if (!this.groupByRestaurant) {
      this.selectedRestaurantGroup = "";
      return;
    }
    if (!merged.length) {
      this.selectedRestaurantGroup = "";
      return;
    }
    if (!merged.includes(this.selectedRestaurantGroup)) {
      this.selectedRestaurantGroup = merged[0];
    }
  }

  applyWaitersFromRaw() {
    if (!this.waitersRawResponse) {
      this.waiters = [];
      this.overallTotalSum = 0;
      this.byRestaurant = false;
      return;
    }
    this.syncRestaurantGroups();
    const parsed = parseWaitersResponse(
      this.waitersRawResponse,
      this.groupByRestaurant,
      this.groupByRestaurant ? this.selectedRestaurantGroup : null,
    );
    this.byRestaurant = parsed.byRestaurant;
    this.setWaiters(parsed.waiters);
    this.setOverallTotalSum(parsed.overallTotalSum);
  }

  async getWaiters(id, dateFrom, dateTo) {
    if (id === "" || id == null) return;
    this.setLoading(true);
    this.setError(false);
    try {
      if (this.groupByRestaurant) {
        const groupsResp = await dataRequest.getIikoOlapData(id, dateFrom, dateTo, true, null);
        this.restaurantGroups = extractRestaurantGroups(groupsResp?.data || []);
        if (
          this.restaurantGroups.length &&
          !this.restaurantGroups.includes(this.selectedRestaurantGroup)
        ) {
          this.selectedRestaurantGroup = this.restaurantGroups[0];
        }
      }

      const group =
        this.groupByRestaurant && this.selectedRestaurantGroup
          ? this.selectedRestaurantGroup
          : null;
      const response = await dataRequest.getWaitersDatas(
        id,
        dateFrom,
        dateTo,
        Boolean(group),
        group,
      );
      this.waitersRawResponse = response;
      this.applyWaitersFromRaw();
    } catch (error) {
      console.log(error);
      this.setError(true);
      this.waitersRawResponse = null;
      this.waiters = [];
      this.overallTotalSum = 0;
    } finally {
      this.setLoading(false);
    }
  }
}

const waitersStore = new WaitersStore();

export default waitersStore;
